
import { useState } from "react";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface CommentsDialogProps {
  floorPlanId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface Comment {
  id: string;
  comment: string;
  created_at: string;
  floor_plan_id: string;
  user_id: string;
  user: {
    display_name: string | null;
    username: string | null;
    avatar_url: string | null;
  } | null;
}

export function CommentsDialog({ floorPlanId, open, onOpenChange }: CommentsDialogProps) {
  const [newComment, setNewComment] = useState("");
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();

  const { data: comments = [], isError } = useQuery<Comment[]>({
    queryKey: ['floor-plan-comments', floorPlanId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('floor_plan_comments')
        .select(`
          id,
          comment,
          created_at,
          floor_plan_id,
          user_id,
          user:profiles!user_id(
            display_name,
            username,
            avatar_url
          )
        `)
        .eq('floor_plan_id', floorPlanId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching comments:', error);
        throw error;
      }

      // Ensure the data matches our Comment interface
      const typedData = (data || []).map(comment => ({
        ...comment,
        user: comment.user || null
      })) as Comment[];

      return typedData;
    },
  });

  const handleAddComment = async () => {
    if (!user) {
      toast({
        title: "Niet ingelogd",
        description: "Je moet ingelogd zijn om een reactie te kunnen plaatsen.",
        variant: "destructive",
      });
      return;
    }

    if (newComment.trim()) {
      try {
        const { error } = await supabase
          .from('floor_plan_comments')
          .insert({
            floor_plan_id: floorPlanId,
            comment: newComment.trim(),
            user_id: user.id
          });

        if (error) throw error;

        // Reset input and invalidate query to refresh comments
        setNewComment("");
        queryClient.invalidateQueries({ queryKey: ['floor-plan-comments', floorPlanId] });
        
        toast({
          title: "Reactie geplaatst",
          description: "Je reactie is succesvol geplaatst.",
        });
      } catch (error) {
        console.error('Error posting comment:', error);
        toast({
          title: "Fout",
          description: "Er is een fout opgetreden bij het plaatsen van je reactie.",
          variant: "destructive",
        });
      }
    }
  };

  if (isError) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogTrigger asChild>
          <Button variant="outline" className="w-full">
            <MessageSquare className="mr-2 h-4 w-4" />
            Toon reacties
          </Button>
        </DialogTrigger>
        <DialogContent>
          <p className="text-center text-red-500">Er is een fout opgetreden bij het laden van de reacties.</p>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full">
          <MessageSquare className="mr-2 h-4 w-4" />
          Toon reacties ({comments.length})
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Reacties</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <textarea
              className="w-full min-h-[100px] p-2 border rounded-md"
              placeholder="Schrijf een reactie..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
            />
            <Button 
              className="mt-2" 
              onClick={handleAddComment}
              disabled={!newComment.trim()}
            >
              Plaats reactie
            </Button>
          </div>
          <div className="space-y-4">
            {comments.map((comment) => (
              <div key={comment.id} className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={comment.user?.avatar_url || undefined} />
                    <AvatarFallback>
                      {(comment.user?.display_name || comment.user?.username || "?")[0].toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="font-medium">
                    {comment.user?.display_name || comment.user?.username || "Anoniem"}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {format(new Date(comment.created_at), "d MMM yyyy", { locale: nl })}
                  </span>
                </div>
                <p>{comment.comment}</p>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
